import React, { useState, useContext, useEffect } from 'react';
import './builder.css';
import LANGS, { LANG_OPTIONS } from './data/i18n.js';
import { DEFAULT_CONFIG, tmplById } from './data/defaults.js';
// import LandingPage from '../components/sections/LandingPage.jsx';
import Step1 from './steps/Step1.jsx';
import Step2 from './steps/Step2.jsx';
import Step3 from './steps/Step3.jsx';
import Step4 from './steps/Step4.jsx';
import Step5Preview from './steps/Step5Preview.jsx';
import Step6Package from './steps/Step6Package.jsx';
import Step7Domain from './steps/Step7Domain.jsx';
import Step8Review from './steps/Step8Review.jsx';
import BuildingScreen from './BuildingScreen.jsx';
import SitePreviewModal from './SitePreviewModal.jsx';
import PaymentScreen from './PaymentScreen.jsx';
import SuccessScreen from './SuccessScreen.jsx';
import BuildErrorScreen from './BuildErrorScreen.jsx';
import { pkgById } from './data/defaults.js';
import { ADDONS, PACKAGES } from './data/packages.js';
import axios from 'axios';
import { LangCtx } from "../context/LangContext.jsx";

// export const LangContext = createContext('en');
// export const useT = () => LANGS[useContext(LangContext)] || LANGS.en;
const BUILDER_LANG_MAP = {
  "en-us": "en",
};
// LangCtx only provides the page language string — the builder keeps its own
// independent language state so the LangSelect inside steps can change it.
const BuilderLangCtx = React.createContext('en');

export const useT = () => {
  const lang = useContext(BuilderLangCtx);
  const builderLang = BUILDER_LANG_MAP[lang] || lang;
  return LANGS[builderLang] || LANGS.en;
};

export default function BuilderApp({ startAtWizard = false, onExit }) {
  const ctxLang = useContext(LangCtx);
  const BASE = import.meta.env.VITE_API_BASE || ''
  const SALEBASE = import.meta.env.VITE_API_URL_FOR_SALE || ''

  // Local language state — starts from the page language, changeable inside builder
  const [lang, setLang] = useState(() => BUILDER_LANG_MAP[ctxLang] || ctxLang);

  const builderLang = BUILDER_LANG_MAP[lang] || lang;
  const tr = LANGS[builderLang] || LANGS.en;

  useEffect(() => {
    const opt = LANG_OPTIONS.find(o => o.code === lang);
    document.documentElement.dir = opt && opt.dir === 'rtl' ? 'rtl' : 'ltr';
    document.documentElement.lang = lang;
  }, [lang]);

  // URL parameters
  const urlParams = new URLSearchParams(window.location.search);

  const stepParam = urlParams.get('step');
  const phaseParam = urlParams.get('phase');
  const restaurantIdParam = urlParams.get('restaurantId');

  const [restaurantId, setRestaurantId] = useState(null);
  const [phase, setPhase] = useState('wizard');
  const [step, setStep] = useState(1);

  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [buildResult, setBuildResult] = useState(null); // { live_url, site_id }
  const [previewUrl, setPreviewUrl] = useState(null);
  const [buildError, setBuildError] = useState('');
  const [agreed, setAgreed] = useState(false);

  useEffect(() => {
    /*
     * Apply ?step=
     */
    if (stepParam && !isNaN(stepParam)) {
      const stepNum = parseInt(stepParam, 10);

      if (stepNum >= 1 && stepNum <= 8) {
        setStep(stepNum);
      }
    }

    /*
     * Apply ?phase=
     */
    const validPhases = [
      'landing',
      'wizard',
      'building',
      'sitePreview',
      'payment',
      'success',
      'buildError'
    ];

    if (phaseParam && validPhases.includes(phaseParam)) {
      setPhase(phaseParam);
    } else if (startAtWizard) {
      setPhase('wizard');
    }
  }, [stepParam, phaseParam, startAtWizard]);

  const setField = (key, val) => setConfig(c => ({ ...c, [key]: val }));
  const TOTAL = 8;

  const fetchRestaurantData = async () => {
    if (!restaurantIdParam) return;

    try {
      const response = await axios.get(
        BASE + `/v1/restaurant/${restaurantIdParam}`
      );

      if (response.status !== 200 || !response.data) {
        return;
      }

      const restaurant = response.data;

      setConfig(current => ({
        ...current,

        /*
         * Existing restaurant package
         */
        ...(restaurant.package
          ? {
            package: restaurant.package
          }
          : {}),

        /*
         * Existing addons
         */
        ...(restaurant.payments
          ? {
            addons: Object.entries(restaurant.payments)
              .filter(([, payment]) => payment?.enabled === true)
              .map(([id]) => id)
          }
          : {}),

        /*
         * Existing owner email
         */
        owner_email: restaurant.email || '',

        /*
         * If your API returns branding,
         * you can load it here too.
         */
        ...(restaurant.branding
          ? {
            branding:
              typeof restaurant.branding === 'string'
                ? JSON.parse(restaurant.branding)
                : restaurant.branding
          }
          : {})
      }));

      setRestaurantId(restaurantIdParam);

    } catch (error) {
      console.error(
        'Error fetching restaurant data:',
        error
      );
    }
  };

  useEffect(() => {
    fetchRestaurantData();
  }, [restaurantIdParam]);


  const next = () => {
    if (step < TOTAL) setStep(s => s + 1);
    else setPhase('building');
  };
  const back = () => {
    if (step > 1) setStep(s => s - 1);
    else {
      onExit?.();
    }
  };

  const stepProps = { config, setField, lang, setLang, next, back };

  const t = tr;

  const handleBuild = async () => {
    try {
      const pkg = pkgById(config.package);
      const pkgIdx = PACKAGES.findIndex(p => p.id === pkg.id);
      const pd = t.pkgs[pkgIdx];

      const payload = {
        ...config,
        package: pkgById(config.package),
        addons: (config.addons || [])
          .map(id => ADDONS.find(addon => addon.id === id))
          .filter(Boolean),
        restaurantId: restaurantIdParam,
        saleId: localStorage.getItem("saleAgentId") || "",
      };

      payload.package.price = pd.price;

      /*
       * Create multipart/form-data
       */
      const formData = new FormData();

      /*
       * Remove the File object from the JSON payload.
       *
       * The actual File will be sent separately below.
       */
      const payloadForJson = {
        ...payload,
        branding: {
          ...payload.branding,
          logo_file: undefined,
          logo_dataUrl: undefined,
        },
      };

      /*
       * Add normal builder configuration as JSON.
       */
      formData.append(
        'payload',
        JSON.stringify(payloadForJson)
      );

      /*
       * Add the actual logo File.
       *
       * PHP will receive this as:
       *
       * $_FILES['logo']
       */
      if (config.branding?.logo_file instanceof File) {
        formData.append(
          'logo',
          config.branding.logo_file
        );
      }

      setPhase('building');

      const response = await axios.post(
        BASE + '/v1/build/restaurant',
        formData
      );

      setRestaurantId(response.data);

      const formDataForSale = new FormData();
      formDataForSale.append('organization_number', response.data);
      formDataForSale.append('company_name', config.branding?.company_name || '');
      formDataForSale.append('email', config.owner_email || '');
      formDataForSale.append('website', config.custom_domain || '');
      formDataForSale.append('amount', pd.price);
      formDataForSale.append('saleId', localStorage.getItem("saleAgentId") || "");
      const responseCustomer = await axios.post(
        SALEBASE + '/Api/SalesApiController/saveContractFromRestaurant', formDataForSale
      )

    } catch (err) {
      setBuildError(
        err.message ||
        'Failed to build site. Please try again.'
      );

      setPhase('buildError');
    }
  };


  const handlePay = async () => {
    try {
      const pkg = pkgById(config.package);
      const pkgIdx = PACKAGES.findIndex(p => p.id === pkg.id);
      const pd = t.pkgs[pkgIdx];
      const payload = {
        ...config,
        package: pkgById(config.package),
        addons: (config.addons || [])
          .map(id => ADDONS.find(addon => addon.id === id))
          .filter(Boolean),
        restaurantId: restaurantId,
        saleId: localStorage.getItem("saleAgentId") || "",
      };

      payload.package.price = pd.price;


      console.log('Payment payload:', payload);

      // 1. Create Stripe checkout session
      const response = await axios.post(
        BASE + '/v1/payments/client',
        payload
      );

      if (response.status !== 200 || !response.data?.redirectUrl) {
        setBuildError('Payment failed');
        setPhase('buildError');
        return;
      }

      const redirectUrl = response.data.redirectUrl;

      // 2. Open Stripe checkout
      const popup = window.open(
        redirectUrl,
        'stripe_checkout',
        'width=600,height=800,left=300,top=100'
      );

      if (!popup) {
        setBuildError('Please allow popups to continue with payment.');
        setPhase('buildError');
        return;
      }

      // 3. Listen for Stripe success/cancel
      const listener = async (event) => {

        if (
          event.data?.type ===
          "client-stripe-success"
        ) {

          window.removeEventListener(
            "message",
            listener
          );


          const sessionId =
            event.data.sessionId;


          if (!sessionId) {

            setBuildError(
              "Stripe session ID is missing."
            );

            setPhase("buildError");

            return;
          }


          try {

            const subscriptionPayload = {

              ...payload,

              sessionId,

              restaurantId:
                event.data.restaurantId
            };


            const response =
              await axios.post(
                BASE + '/v1/payments/client/subscription',
                subscriptionPayload
              );


            if (
              response.status !== 200 &&
              response.status !== 201
            ) {

              throw new Error(
                "Subscription creation failed."
              );
            }


            setPhase("success");


          } catch (error) {

            console.error(
              error
            );

            setBuildError(
              error.response?.data?.message ||
              "Subscription creation failed."
            );

            setPhase("buildError");
          }
        }


        if (
          event.data?.type ===
          "stripe-cancel"
        ) {

          window.removeEventListener(
            "message",
            listener
          );

          alert(
            "Betaling ble avbrutt. Vennligst prøv igjen."
          );
        }
      };

      window.addEventListener('message', listener);

    } catch (error) {
      console.error('Payment error:', error);

      setBuildError(
        error.response?.data?.message ||
        'Payment failed'
      );

      setPhase('buildError');
    }
  };

  return (
    <BuilderLangCtx.Provider value={lang}>
      <div className="builder-root">
        <div className="mesh-bg" />
        {/* {phase === 'landing' && (
        <LandingPage onStart={() => { setPhase('wizard'); setStep(1); }} lang={lang} setLang={setLang} />
      )} */}
        {phase === 'wizard' && (
          <>
            {step === 1 && <Step1 {...stepProps} />}
            {step === 2 && <Step2 {...stepProps} />}
            {step === 3 && <Step3 {...stepProps} />}
            {step === 4 && <Step4 {...stepProps} />}
            {step === 5 && <Step5Preview {...stepProps} />}
            {step === 6 && <Step6Package {...stepProps} />}
            {step === 7 && <Step7Domain {...stepProps} />}
            {step === 8 && (
              <Step8Review
                {...stepProps}
                onBuild={handleBuild}
                agreed={agreed}
                setAgreed={setAgreed}
              />
            )}
          </>
        )}
        {phase === 'building' && (
          <BuildingScreen
            config={config}
            lang={lang}
            onDone={(result) => {
              setBuildResult(result);
              const tmpl = tmplById(config.template);
              setPreviewUrl(tmpl.demoUrl || result.live_url);
              setPhase('sitePreview');
              window.location.href = 'sale/customers.php';
            }}
            onError={(msg) => { setBuildError(msg || 'Build failed'); setPhase('buildError'); }}
          />
        )}
        {phase === 'sitePreview' && (
          <SitePreviewModal
            url={previewUrl}
            onClose={() => { setPhase('wizard'); setStep(8); }}
            onProceed={() => setPhase('payment')}
          />
        )}
        {phase === 'payment' && (
          <PaymentScreen
            config={config}
            lang={lang}
            buildResult={buildResult}
            onPay={handlePay}
            onBack={() => setPhase('sitePreview')}
          />
        )}
        {phase === 'success' && (
          <SuccessScreen config={config} lang={lang} buildResult={buildResult} />
        )}
        {phase === 'buildError' && (
          <BuildErrorScreen buildError={buildError} onBack={() => { setBuildError(''); setPhase('wizard'); setStep(8); }} />
        )}
      </div>
    </BuilderLangCtx.Provider>
  );
}
